import { isNullUndefined } from '@uefadigital/panenka';

import { hydrateWithTranslations } from '@services/TranslatorService';
import type {
  Player,
  PlayerEvent,
  LineupsTeam,
  LineupsPlayer,
  Match,
  PlayerStatus,
  PlayerInfo,
} from '@shared-types/match.types';
import { EventType, EventSubType, CoachRole } from '@shared-types/match.types';
import { matchTacticalLineupsAvailable } from '@utilities/live';

export const getCoach = (teamLineup: LineupsTeam) => {
  const coach =
    teamLineup?.coaches?.find((c) => c.role === CoachRole.COACH) ??
    teamLineup?.coaches?.find((c) => c.role === CoachRole.ASSISTANT_COACH) ??
    {};

  return coach;
};

export const getCoachPerson = (teamLineup: LineupsTeam) => {
  return hydrateWithTranslations(getCoach(teamLineup).person);
};

export const getCoachRole = (teamLineup: LineupsTeam) => {
  return hydrateWithTranslations(getCoach(teamLineup));
};

export const getPlayerPosition = ({ fspFieldCoordinate }: LineupsPlayer) => {
  if (!fspFieldCoordinate) return null;

  const { x, y } = fspFieldCoordinate;
  if (isNullUndefined(x) || isNullUndefined(y)) return null;

  return {
    top: `${100 - (5250 - x) / 105}%`,
    left: `${100 - (3400 - y) / 68}%`,
  };
};

export const showTacticalLineups = (match: Match, homeLineup: LineupsTeam, awayLineup: LineupsTeam) =>
  matchTacticalLineupsAvailable(match) &&
  homeLineup?.field?.every(getPlayerPosition) &&
  awayLineup?.field?.every(getPlayerPosition);

export const getEventsByTypes = (events: PlayerEvent[], types: EventType[]) => {
  const fltevents = events?.filter((ev) => types.includes(EventType[ev.type])) ?? [];

  return fltevents?.toSorted(
    (a, b) =>
      (a.time?.minute ?? 0) - (b.time?.minute ?? 0) ||
      (a.time?.second ?? 0) - (b.time?.second ?? 0) ||
      (a.time?.injuryMinute ?? 0) - (b.time?.injuryMinute ?? 0),
  );
};

const getPlayerEvents = (events: PlayerEvent[], id: string, types: EventType[]) =>
  getEventsByTypes(events, types).filter((ev) => ev.primaryActor?.person?.id === id);

const getSubstitutionEvents = (events: PlayerEvent[], id: string, isBench?: boolean) =>
  getEventsByTypes(events, [EventType.SUBSTITUTION])
    .filter((ev) => ev.primaryActor?.person?.id === id || (isBench && ev.secondaryActor?.person?.id === id))
    .map((ev) => ({
      ...ev,
      type: (ev.primaryActor?.person?.id === id ? 'out' : 'in') as 'out' | 'in',
    }));

export const getPlayerGoals = (player: Player, events: PlayerEvent[]) => {
  const goals = getPlayerEvents(events, player.id, [EventType.GOAL]);

  const ownGoalsNumber = goals.filter((g) => g.subType === EventSubType.OWN).length;

  const goalsNumber = goals.length - ownGoalsNumber;

  return { goalsNumber, ownGoalsNumber };
};

export const getPlayerCards = (player: Player, events: PlayerEvent[]) => {
  const redYellowCards = getPlayerEvents(events, player.id, [EventType.RED_YELLOW_CARD]).length;
  if (redYellowCards > 0) return 'yellow-red';

  const redCards = getPlayerEvents(events, player.id, [EventType.RED_CARD]).length;
  if (redCards > 0) return 'red';

  const yellowCards = getPlayerEvents(events, player.id, [EventType.YELLOW_CARD]).length;
  if (yellowCards > 0) return 'yellow';
};

export const getPlayerSubstitutions = (player: Player, events: PlayerEvent[], isBench?: boolean) => {
  const substitutions = getSubstitutionEvents(events, player.id, isBench);
  if (substitutions.length === 0) return;

  return substitutions[0].type;
};

export const getAbsenceReason = (playerStatus: PlayerStatus): 'suspended' | 'injury' | 'doubtful' | null => {
  if (!playerStatus || (!playerStatus.isSuspended && playerStatus.healthStatus === 'FIT')) {
    return null;
  }
  if (playerStatus.isSuspended) {
    return 'suspended';
  }

  return playerStatus.healthStatus === 'INJURED' ? 'injury' : 'doubtful';
};

function mapUnavailablePlayerToLineupFormat() {
  return (playerInfo: PlayerInfo): LineupsPlayer => {
    return {
      player: playerInfo.player,
      type: 'PLAYER',
      fspFieldCoordinate: { x: -1, y: -1 },
      jerseyNumber: null,
      status: playerInfo.status,
    };
  };
}

export function splitUnavailablePlayersToTeams(params: {
  unavailablePlayers: PlayerInfo[];
  homeLineup: LineupsTeam;
  awayLineup: LineupsTeam;
  isClubMatch: boolean;
}): { homeUnavailable: LineupsPlayer[]; awayUnavailable: LineupsPlayer[] } {
  const { awayLineup, homeLineup, isClubMatch, unavailablePlayers } = params;

  const mapUnavailablePlayer = mapUnavailablePlayerToLineupFormat();

  const homeUnavailable = unavailablePlayers
    .filter((p) => (isClubMatch ? p.player.clubId : p.player.nationalTeamId) === homeLineup.team.id)
    .map(mapUnavailablePlayer);
  const awayUnavailable = unavailablePlayers
    .filter((p) => (isClubMatch ? p.player.clubId : p.player.nationalTeamId) === awayLineup.team.id)
    .map(mapUnavailablePlayer);

  return { homeUnavailable, awayUnavailable };
}
